import { create } from 'zustand';
import {
  addDays,
  endOfDay,
  endOfWeek,
  max,
  min,
  startOfDay,
  startOfWeek,
  subDays,
  subWeeks,
  subYears,
} from 'date-fns';
import { formatInTimeZone, fromZonedTime, toDate, toZonedTime } from 'date-fns-tz';
import { storage } from '@/lib/storage';
import { TIMEZONE } from '@/lib/format';
import { useSettingsStore, type BIUserSettingsBlob } from '@/api/bi/settings';

/**
 * Push a slice of changes through to the server-backed settings store.
 * Cheap to call repeatedly — the store debounces PATCHes.
 */
function pushSettingsPatch(partial: BIUserSettingsBlob) {
  try {
    useSettingsStore.getState().patchLocal(partial);
  } catch {
    /* noop — settings store may not be ready (logged-out boot, tests). */
  }
}

/** Mirror the current default/recent store cache up to the server. */
function syncStoreSlice() {
  pushSettingsPatch({
    default_store: storage.getDefaultStore(),
    recent_stores: storage.getRecentStores(),
  });
}

export type RangePreset =
  | 'today'
  | 'yesterday'
  | 'this-week'
  | 'last-week'
  | '7d'
  | '30d'
  | 'mtd'
  | 'custom';
export type CompareMode = 'previous-period' | 'previous-year' | 'none';
export type TimeRangeMode = 'standard' | 'business';

export interface DateRange {
  from: Date;
  to: Date;
  preset: RangePreset;
}

/**
 * "Today" is a calendar claim, and the calendar that counts is the one the
 * business runs on.
 *
 * The date-fns day and week helpers read a Date's *local* fields, so calling
 * them on a plain `new Date()` draws every boundary in whatever timezone the
 * viewer's machine happens to be set to. Everything downstream — KPI headers,
 * chart axes, the business-day branch in `rangeISO` — is formatted in
 * `TIMEZONE`, so the two only agree for a user sitting in Kathmandu. For
 * anyone else "Today" quietly selected a different day's data than the page
 * claimed to be showing, and business mode silently degraded to standard
 * because its boundaries no longer landed on local midnight.
 *
 * So: shift into `TIMEZONE` wall-clock, do the arithmetic there, shift back.
 */
export function rangeFromPreset(preset: Exclude<RangePreset, 'custom'>): DateRange {
  // Local fields of `now` now carry the Kathmandu date and time.
  const now = toZonedTime(new Date(), TIMEZONE);
  // Sunday-anchored week to match the user's calendar mental model
  // ("this week (Sun to Sat)" / "last week").
  const weekStartsOn = 0 as const;
  const range = (from: Date, to: Date): DateRange => ({
    from: fromZonedTime(from, TIMEZONE),
    to: fromZonedTime(to, TIMEZONE),
    preset,
  });

  switch (preset) {
    case 'today':
      return range(startOfDay(now), endOfDay(now));
    case 'yesterday': {
      const y = subDays(now, 1);
      return range(startOfDay(y), endOfDay(y));
    }
    case 'this-week':
      return range(
        startOfDay(startOfWeek(now, { weekStartsOn })),
        endOfDay(endOfWeek(now, { weekStartsOn })),
      );
    case 'last-week': {
      const lw = subWeeks(now, 1);
      return range(
        startOfDay(startOfWeek(lw, { weekStartsOn })),
        endOfDay(endOfWeek(lw, { weekStartsOn })),
      );
    }
    case '7d':
      return range(startOfDay(subDays(now, 6)), endOfDay(now));
    case '30d':
      return range(startOfDay(subDays(now, 29)), endOfDay(now));
    case 'mtd': {
      const first = new Date(now.getFullYear(), now.getMonth(), 1);
      return range(startOfDay(first), endOfDay(now));
    }
  }
}

/** Interprets yyyy-MM-dd strings as calendar days in `TIMEZONE` (same as KPI date display). */
export function customRangeFromYmd(fromYmd: string, toYmd: string): DateRange | null {
  const s0 = toDate(`${fromYmd}T00:00:00`, { timeZone: TIMEZONE });
  const s1 = toDate(`${toYmd}T00:00:00`, { timeZone: TIMEZONE });
  if (Number.isNaN(s0.getTime()) || Number.isNaN(s1.getTime())) return null;
  const startDay = min([s0, s1]);
  const endDay = max([s0, s1]);
  const endYmd = formatInTimeZone(endDay, TIMEZONE, 'yyyy-MM-dd');
  const from = toDate(`${formatInTimeZone(startDay, TIMEZONE, 'yyyy-MM-dd')}T00:00:00`, { timeZone: TIMEZONE });
  const to = toDate(`${endYmd}T23:59:59.999`, { timeZone: TIMEZONE });
  return { from, to, preset: 'custom' };
}

/**
 * Build a custom range from `yyyy-MM-ddTHH:mm` strings (the native value of
 * `<input type="datetime-local">`) interpreted in `TIMEZONE`. Used by the
 * date+time picker so the user can scope reports down to e.g. the lunch
 * rush. Backends accept ISO datetimes for `start` / `end` so no extra
 * server change is needed.
 */
export function customRangeFromDateTime(
  fromLocal: string,
  toLocal: string,
): DateRange | null {
  if (!fromLocal || !toLocal) return null;
  // `datetime-local` values come back as `yyyy-MM-ddTHH:mm` (no seconds, no
  // tz). Append `:00` so `toDate` parses cleanly.
  const a = toDate(normalizeLocal(fromLocal), { timeZone: TIMEZONE });
  const b = toDate(normalizeLocal(toLocal), { timeZone: TIMEZONE });
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const from = a.getTime() <= b.getTime() ? a : b;
  const to = a.getTime() <= b.getTime() ? b : a;
  return { from, to, preset: 'custom' };
}

function normalizeLocal(s: string): string {
  // Accept `yyyy-MM-ddTHH:mm` and `yyyy-MM-ddTHH:mm:ss[.SSS]`.
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(s)) return `${s}:00`;
  return s;
}

/**
 * Build a custom range from arbitrary ISO datetime strings (with or without
 * a timezone suffix). Strings without a tz are treated as `TIMEZONE` local.
 * Used by `hydrateFromQuery` to round-trip URL params written by
 * `filtersToQuery`.
 */
export function customRangeFromIso(fromIso: string, toIso: string): DateRange | null {
  if (!fromIso || !toIso) return null;
  const a = parseFlexibleIso(fromIso);
  const b = parseFlexibleIso(toIso);
  if (!a || !b) return null;
  const from = a.getTime() <= b.getTime() ? a : b;
  const to = a.getTime() <= b.getTime() ? b : a;
  return { from, to, preset: 'custom' };
}

function parseFlexibleIso(s: string): Date | null {
  // `Z` or `+HH:MM` suffix → absolute datetime, parse directly.
  if (/(Z|[+\-]\d{2}:?\d{2})$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // No tz → interpret as wall-clock time in `TIMEZONE`.
  const normalized = normalizeLocal(s);
  const d = toDate(normalized, { timeZone: TIMEZONE });
  return Number.isNaN(d.getTime()) ? null : d;
}

/** True if the range carries non-midnight times (so we should keep them in URLs). */
export function rangeHasTimeOfDay(range: DateRange): boolean {
  const fromYmd = formatInTimeZone(range.from, TIMEZONE, 'HH:mm:ss.SSS');
  const toYmd = formatInTimeZone(range.to, TIMEZONE, 'HH:mm:ss.SSS');
  return fromYmd !== '00:00:00.000' || toYmd !== '23:59:59.999';
}

/**
 * Compute the comparison range for a given primary range + compare mode.
 *
 *   previous-period → same local clock span in the prior selected period.
 *   previous-year   → same window, shifted back exactly one year.
 *   none            → null (caller must skip the compare).
 *
 * We hand back ISO strings so the caller can drop them straight into BI hooks.
 */
export interface CompareRange {
  start: string;
  end: string;
}

export function compareRangeFor(
  range: DateRange,
  mode: CompareMode,
  timeRangeMode: TimeRangeMode = 'standard',
  now: Date = new Date(),
): CompareRange | null {
  if (mode === 'none') return null;
  const current = rangeISO(range, timeRangeMode);
  if (mode === 'previous-year') {
    return {
      start: subYears(new Date(current.start), 1).toISOString(),
      end: subYears(new Date(current.end), 1).toISOString(),
    };
  }

  const startMs = new Date(current.start).getTime();
  const endDate = new Date(current.end);
  const endMs = endDate.getTime();
  const shiftMs = nominalWindowMs(startMs, endDate);
  const effectiveEndMs = liveCappedEndMs(startMs, endMs, now);
  const prevStart = new Date(startMs - shiftMs);
  const prevEnd = new Date(effectiveEndMs - shiftMs);
  return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
}

function nominalWindowMs(startMs: number, endDate: Date): number {
  const inclusiveEndAdjustment = endDate.getMilliseconds() === 999 ? 1 : 0;
  return Math.max(1, endDate.getTime() - startMs + inclusiveEndAdjustment);
}

function liveCappedEndMs(startMs: number, endMs: number, now: Date): number {
  const nowMs = Math.floor(now.getTime() / 60_000) * 60_000;
  if (startMs <= nowMs && nowMs < endMs) return nowMs;
  return endMs;
}

interface FiltersState {
  range: DateRange;
  /**
   * Single-store accessor. Kept around for back-compat with every page that
   * was authored before multi-store landed. Derived rule:
   *
   *   storeIds.length === 0 → null  (all stores)
   *   storeIds.length === 1 → that one id
   *   storeIds.length  >  1 → null  (caller must opt into multi via storeIds
   *                                  + a fan-out hook; the legacy single-id
   *                                  hooks safely fall back to the union)
   *
   * Mutating this via `setStoreId` collapses `storeIds` accordingly so the
   * two stay in sync.
   */
  storeId: number | null;
  /** Source of truth for store scope. Empty array = all stores. */
  storeIds: number[];
  compareMode: CompareMode;
  timeRangeMode: TimeRangeMode;
  setRange: (r: DateRange) => void;
  setPreset: (preset: Exclude<RangePreset, 'custom'>) => void;
  setCustomFromYmd: (fromYmd: string, toYmd: string) => boolean;
  /** Apply a date+time custom range (input value is `yyyy-MM-ddTHH:mm`). */
  setCustomFromDateTime: (fromLocal: string, toLocal: string) => boolean;
  setStoreId: (id: number | null) => void;
  /** Replace the multi-store selection wholesale. */
  setStoreIds: (ids: number[]) => void;
  /** Toggle a single store in the selection. */
  toggleStoreId: (id: number) => void;
  /** Clear store filter (back to "all stores"). */
  clearStores: () => void;
  setCompareMode: (mode: CompareMode) => void;
  setTimeRangeMode: (mode: TimeRangeMode) => void;
  /** Replace state from a URL query string (used on app boot + back/forward). */
  hydrateFromQuery: (search: string) => void;
}

const VALID_PRESETS: RangePreset[] = [
  'today',
  'yesterday',
  'this-week',
  'last-week',
  '7d',
  '30d',
  'mtd',
  'custom',
];

function presetFromQuery(v: string | null): RangePreset | null {
  return VALID_PRESETS.includes(v as RangePreset) ? (v as RangePreset) : null;
}

function compareFromQuery(v: string | null): CompareMode | null {
  if (v === 'previous-period' || v === 'previous-year' || v === 'none') return v;
  return null;
}

function timeRangeModeFromQuery(v: string | null): TimeRangeMode | null {
  if (v === 'standard' || v === 'business') return v;
  return null;
}

// Pull the user's saved default range/compare so the very first paint matches
// their last preference (the FilterUrlSync still wins if a `?range=` param is
// present in the URL).
const PREFS_INIT = storage.getPrefs();
const INITIAL_PRESET: Exclude<RangePreset, 'custom'> = PREFS_INIT.default_range ?? '7d';
const INITIAL_COMPARE: CompareMode =
  storage.getCompareMode() ?? PREFS_INIT.default_compare ?? 'previous-period';
const INITIAL_TIME_RANGE_MODE: TimeRangeMode =
  timeRangeModeFromQuery(PREFS_INIT.time_range_mode) ?? 'standard';

// Snapshot of the URL at module-import time so the post-login settings
// hydrator can tell whether the user actually deep-linked with a `?range=`
// param (in which case we must respect it) or whether the param was
// auto-written by `FilterUrlSync` after we picked a default.
const INITIAL_URL_SEARCH =
  typeof window !== 'undefined' ? window.location.search : '';
const INITIAL_URL_HAD_RANGE = (() => {
  try {
    return new URLSearchParams(INITIAL_URL_SEARCH).has('range');
  } catch {
    return false;
  }
})();
const INITIAL_URL_HAD_DAY = (() => {
  try {
    return new URLSearchParams(INITIAL_URL_SEARCH).has('day');
  } catch {
    return false;
  }
})();

/**
 * sessionStorage key that `FilterUrlSync` updates on every URL it writes.
 * If, on cold boot, the current URL matches the last-written stamp, then
 * the `?range=` param almost certainly came from us (not a deep-link), so
 * the post-login settings hydrator is free to overwrite it with the user's
 * saved default. New-tab deep-links have an empty sessionStorage, so the
 * URL wins as expected.
 */
export const URL_SYNC_STAMP_KEY = 'fasto-bi:url-stamp';
const INITIAL_URL_AUTO_WRITTEN = (() => {
  try {
    if (typeof sessionStorage === 'undefined') return false;
    const stamp = sessionStorage.getItem(URL_SYNC_STAMP_KEY);
    if (stamp == null) return false;
    return stamp === INITIAL_URL_SEARCH.replace(/^\?/, '');
  } catch {
    return false;
  }
})();

function deriveStoreId(ids: number[]): number | null {
  return ids.length === 1 ? ids[0] : null;
}

const INITIAL_STORE_ID = storage.getDefaultStore();
const INITIAL_STORE_IDS: number[] = INITIAL_STORE_ID != null ? [INITIAL_STORE_ID] : [];

export const useFilters = create<FiltersState>((set, get) => ({
  range: rangeFromPreset(INITIAL_PRESET),
  storeId: INITIAL_STORE_ID,
  storeIds: INITIAL_STORE_IDS,
  compareMode: INITIAL_COMPARE,
  timeRangeMode: INITIAL_TIME_RANGE_MODE,
  setRange: (range) => set({ range }),
  setPreset: (preset) => set({ range: rangeFromPreset(preset) }),
  setCustomFromYmd: (fromYmd, toYmd) => {
    const next = customRangeFromYmd(fromYmd, toYmd);
    if (!next) return false;
    set({ range: next });
    return true;
  },
  setCustomFromDateTime: (fromLocal, toLocal) => {
    const next = customRangeFromDateTime(fromLocal, toLocal);
    if (!next) return false;
    set({ range: next });
    return true;
  },
  setStoreId: (storeId) => {
    storage.setDefaultStore(storeId);
    storage.pushRecentStore(storeId);
    syncStoreSlice();
    const ids = storeId == null ? [] : [storeId];
    set({ storeId, storeIds: ids });
  },
  setStoreIds: (ids) => {
    const deduped = Array.from(new Set(ids.filter((n) => Number.isFinite(n) && n > 0)));
    const single = deriveStoreId(deduped);
    storage.setDefaultStore(single);
    if (single != null) storage.pushRecentStore(single);
    syncStoreSlice();
    set({ storeIds: deduped, storeId: single });
  },
  toggleStoreId: (id) => {
    if (!Number.isFinite(id) || id <= 0) return;
    const cur = get().storeIds;
    const next = cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id];
    get().setStoreIds(next);
  },
  clearStores: () => {
    storage.setDefaultStore(null);
    syncStoreSlice();
    set({ storeIds: [], storeId: null });
  },
  setCompareMode: (mode) => {
    storage.setCompareMode(mode);
    pushSettingsPatch({ compare_mode: mode });
    set({ compareMode: mode });
  },
  setTimeRangeMode: (timeRangeMode) => {
    storage.setPrefs({ time_range_mode: timeRangeMode });
    pushSettingsPatch({ prefs: { time_range_mode: timeRangeMode } });
    set({ timeRangeMode });
  },
  hydrateFromQuery: (search) => {
    const params = new URLSearchParams(search);
    const update: Partial<FiltersState> = {};

    const presetRaw = presetFromQuery(params.get('range'));
    if (presetRaw && presetRaw !== 'custom') {
      update.range = rangeFromPreset(presetRaw);
    } else if (presetRaw === 'custom') {
      const from = params.get('from');
      const to = params.get('to');
      if (from && to) {
        // Accept both `yyyy-MM-dd` (date-only legacy form) and full ISO
        // datetimes (`yyyy-MM-ddTHH:mm[:ss[.SSS][Z|+05:45]]`). The presence
        // of a `T` is the cheapest reliable discriminator.
        const isDateTime = from.includes('T') || to.includes('T');
        const next = isDateTime
          ? customRangeFromIso(from, to)
          : customRangeFromYmd(from, to);
        if (next) update.range = next;
      }
    }

    const storeRaw = params.get('store');
    if (storeRaw === 'all' || storeRaw === '') {
      update.storeIds = [];
      update.storeId = null;
    } else if (storeRaw != null) {
      // Accept either `store=1` or `store=1,2,3` so saved views and the
      // multi-picker can both round-trip.
      const ids = storeRaw
        .split(',')
        .map((s) => Number(s.trim()))
        .filter((n) => Number.isFinite(n) && n > 0);
      if (ids.length > 0) {
        update.storeIds = Array.from(new Set(ids));
        update.storeId = deriveStoreId(update.storeIds!);
      }
    }

    const compare = compareFromQuery(params.get('compare'));
    if (compare) update.compareMode = compare;

    const timeRangeMode = timeRangeModeFromQuery(params.get('day'));
    if (timeRangeMode) update.timeRangeMode = timeRangeMode;

    if (Object.keys(update).length > 0) set(update as FiltersState);
  },
}));

export function rangeISO(
  range: DateRange,
  timeRangeMode: TimeRangeMode = 'standard',
): { start: string; end: string } {
  if (timeRangeMode === 'business' && !rangeHasTimeOfDay(range)) {
    const fromYmd = formatInTimeZone(range.from, TIMEZONE, 'yyyy-MM-dd');
    const toYmd = formatInTimeZone(range.to, TIMEZONE, 'yyyy-MM-dd');
    const dayAfterTo = formatInTimeZone(
      addDays(toDate(`${toYmd}T00:00:00`, { timeZone: TIMEZONE }), 1),
      TIMEZONE,
      'yyyy-MM-dd',
    );

    return {
      start: toDate(`${fromYmd}T06:00:00.000`, { timeZone: TIMEZONE }).toISOString(),
      end: toDate(`${dayAfterTo}T05:59:59.999`, { timeZone: TIMEZONE }).toISOString(),
    };
  }
  return { start: range.from.toISOString(), end: range.to.toISOString() };
}

/**
 * Apply the user's server-backed `prefs.default_range` / `prefs.default_compare`
 * to the live filter store *once*, after the post-login settings GET resolves.
 *
 * Module-level `PREFS_INIT` is read before `setStorageUser(sub)` runs, so on
 * cold boot the wrong namespace is in play and we end up with the global
 * defaults. This helper re-applies the server values, but only when:
 *
 *   - the URL did not carry a `?range=` deep-link at boot, and
 *   - the user hasn't navigated to a `custom` range in the meantime.
 *
 * Returns true when the store was updated, mainly for tests.
 */
let serverDefaultsApplied = false;
export function applyServerDefaultsToFilters(blob: {
  prefs?: {
    default_range?: Exclude<RangePreset, 'custom'> | null;
    default_compare?: CompareMode | null;
    time_range_mode?: TimeRangeMode | null;
  };
  compare_mode?: CompareMode | null;
}): boolean {
  if (serverDefaultsApplied) return false;
  serverDefaultsApplied = true;

  let touched = false;
  const store = useFilters.getState();

  const wantPreset = blob.prefs?.default_range ?? null;
  // Apply when the URL didn't carry a deep-linked range OR the URL was
  // auto-written by `FilterUrlSync` in a prior session of this same tab
  // (sessionStorage stamp matches). This guarantees that changing the
  // saved default takes effect on the very next reload, while still
  // respecting cross-tab deep-links shared via Saved Views.
  const urlIsExplicit = INITIAL_URL_HAD_RANGE && !INITIAL_URL_AUTO_WRITTEN;
  if (
    wantPreset &&
    !urlIsExplicit &&
    store.range.preset !== 'custom' &&
    store.range.preset !== wantPreset
  ) {
    useFilters.setState({ range: rangeFromPreset(wantPreset) });
    touched = true;
  }

  const wantCompare = blob.compare_mode ?? blob.prefs?.default_compare ?? null;
  if (wantCompare && store.compareMode !== wantCompare) {
    // Don't push back to the server here — the value just came *from* there.
    storage.setCompareMode(wantCompare);
    useFilters.setState({ compareMode: wantCompare });
    touched = true;
  }

  const wantTimeRangeMode = blob.prefs?.time_range_mode ?? null;
  const dayUrlIsExplicit = INITIAL_URL_HAD_DAY && !INITIAL_URL_AUTO_WRITTEN;
  if (wantTimeRangeMode && !dayUrlIsExplicit && store.timeRangeMode !== wantTimeRangeMode) {
    storage.setPrefs({ time_range_mode: wantTimeRangeMode });
    useFilters.setState({ timeRangeMode: wantTimeRangeMode });
    touched = true;
  }

  return touched;
}

/** Test-only: clear the "applied" guard so unit tests can re-run the flow. */
export function _resetServerDefaultsGuard() {
  serverDefaultsApplied = false;
}

/**
 * Serialize the current filter state into a URL query string.
 * Designed to round-trip with `hydrateFromQuery`.
 */
export function filtersToQuery(
  state: Pick<FiltersState, 'range' | 'storeIds' | 'compareMode' | 'timeRangeMode'>,
): string {
  const params = new URLSearchParams();
  params.set('range', state.range.preset);
  params.set('day', state.timeRangeMode);
  if (state.range.preset === 'custom') {
    // Keep URLs short for date-only ranges; only emit the full ISO datetime
    // when the user actually picked a time-of-day. Local (no-tz) format so
    // saved views stay readable; `customRangeFromIso` parses both shapes.
    if (rangeHasTimeOfDay(state.range)) {
      params.set(
        'from',
        formatInTimeZone(state.range.from, TIMEZONE, "yyyy-MM-dd'T'HH:mm"),
      );
      params.set(
        'to',
        formatInTimeZone(state.range.to, TIMEZONE, "yyyy-MM-dd'T'HH:mm"),
      );
    } else {
      params.set('from', formatInTimeZone(state.range.from, TIMEZONE, 'yyyy-MM-dd'));
      params.set('to', formatInTimeZone(state.range.to, TIMEZONE, 'yyyy-MM-dd'));
    }
  }
  if (state.storeIds.length === 0) {
    params.set('store', 'all');
  } else if (state.storeIds.length === 1) {
    params.set('store', String(state.storeIds[0]));
  } else {
    params.set('store', state.storeIds.join(','));
  }
  if (state.compareMode !== 'previous-period') {
    params.set('compare', state.compareMode);
  }
  return params.toString();
}

/**
 * Build the BI `store_id` query value for an HTTP call. Empty array → null
 * so callers can spread `storeId: scope` into existing single-store hooks
 * without code changes; multi-select callers should use the array directly.
 */
export function storeScopeForBI(storeIds: number[]): string | null {
  if (!storeIds.length) return null;
  if (storeIds.length === 1) return String(storeIds[0]);
  return storeIds.join(',');
}

// Re-export for convenient consumer imports.
export { addDays };
